<template>
	<div class="flex flex-col" style="width: 100%; height: calc(100vh - 180px); min-height: 450px;">
		<div v-if="!isLoading" class="flex-grow overflow-y-auto pr-4">
			<div v-if="imageToRotate" class="rotation-workspace">
				<div class="font-bold text-lg mb-4">
					Foto ausrichten
				</div>

				<div class="rotation-preview">
					<BaseImage :src="imageToRotate" :alt="$t('photos')" :rotation="rotation" :show-rounded="false" @tempDoc="setRotatedImage" class="w-full" />
				</div>

				<div class="flex items-center justify-center mt-5">
					<div class="mr-4">
						{{ $t('rotate_image_hint') }}
					</div>

					<button type="button" class="h-8 cursor-pointer" @click="rotateImage">
						<font-awesome-icon :icon="['fas', 'sync-alt']" class="text-connectProBlue" size="lg" />
					</button>
				</div>
			</div>

			<div v-else>
				<div v-if="isEditable" class="flex justify-end mb-6">
					<BaseFileUploader @input="selectImage" accept="image/png, image/jpeg" :multiple="false">
						<BaseButton>
							<font-awesome-icon :icon="['fas', 'upload']" class="text-gray-700 mr-2" />

							{{ $t('upload') }}
						</BaseButton>
					</BaseFileUploader>
				</div>

				<div v-if="visibleImages.length" class="grid grid-cols-2 lg:grid-cols-3 gap-4">
					<div v-for="image in visibleImages" :key="image.localKey" class="relative photo-wrapper">
						<img :src="image.base64content" alt="Foto" class="w-full photo-image cursor-pointer" @click="openImage(image)" />

						<button
							v-if="isEditable && image.canDelete"
							type="button"
							class="photo-delete-btn"
							:title="$t('delete').toString()"
							@click.stop="removeImage(image)"
						>
							<font-awesome-icon :icon="['fas', 'trash']" />
						</button>

						<div v-if="image.isBrokerMedia" class="text-xs text-gray-500 mt-1">
							Vom Makler übermittelt
						</div>
					</div>
				</div>

				<div v-else class="font-bold">
					{{ $t('no_images_transferred') }}
				</div>

				<CoolLightBox :items="lightboxImages" :index="lightboxIndex" @close="lightboxIndex = null" />
			</div>
		</div>

		<div v-else class="flex-grow flex items-center justify-center">
			<LoadingSpinner />
		</div>
		<div v-if="!isLoading && imageToRotate" class="flex justify-end flex-shrink-0 pt-4 mt-4 border-t bg-white">
			<BaseButton class="mr-2" @click="cancelRotation">
				{{ $t('general.cancel') }}
			</BaseButton>

			<SaveButton class="btn-blue" @click="confirmImage">
				{{ $t('add') }}
			</SaveButton>
		</div>
		<div v-else-if="!isLoading" class="flex justify-end flex-shrink-0 pt-4 mt-4 border-t bg-white">
			<BaseButton class="mr-2" :disabled="isSaving" @click="close">
				{{ $t('general.cancel') }}
			</BaseButton>

			<SaveButton class="btn-blue" :disabled="isSaving || !hasChanges" @click="save">
				{{ $t('general.save') }}
			</SaveButton>
		</div>
	</div>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import BaseFileUploader from '@/components/forms/inputs/BaseFileUploader';
import BaseImage from '@/components/forms/BaseImage';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

export default {
	name: 'DamageAssessorCaseEditPhotosModal',

	components: {
		BaseButton,
		SaveButton,
		BaseFileUploader,
		BaseImage,
		LoadingSpinner,
	},

	data() {
		return {
			isLoading: true,
			isSaving: false,
			damageAssessorCase: null,
			pendingImages: [],
			deletedMediaIds: [],
			lightboxIndex: null,
			imageToRotate: null,
			rotation: 0,
			nextLocalId: 1,
		};
	},

	computed: {
		isEditable() {
			return this.damageAssessorCase?.status !== 'closed';
		},
		caseImages() {
			return (this.damageAssessorCase?.media ?? [])
				.filter(item => item.category === null)
				.filter(item => !this.deletedMediaIds.includes(item.id))
				.map(item => ({
					...item,
					localKey: `case-${item.id}`,
					canDelete: true,
					isBrokerMedia: false,
				}));
		},
		brokerImages() {
			return (this.damageAssessorCase?.broker_case?.media ?? [])
				.filter(item => item.category === null)
				.map(item => ({
					...item,

					localKey: `broker-${item.id}`,
					canDelete: false,
					isBrokerMedia: true,
				}));
		},
		newImages() {
			return this.pendingImages.map(item => ({
				...item,
				canDelete: true,
				isBrokerMedia: false,
			}));
		},
		visibleImages() {
			return [...this.caseImages, ...this.brokerImages, ...this.newImages];
		},
		lightboxImages() {
			return this.visibleImages.map(item => item.base64content).filter(Boolean);
		},
		hasChanges() {
			return this.pendingImages.length > 0 || this.deletedMediaIds.length > 0;
		},
	},
	mounted() {
		this.load();
	},
	watch: {
		hasChanges(value) {
			this.$store.commit('setConfirmLeaving', value ? this.$t('leave_page') : false);
		},
	},
	destroyed() {
		this.$store.commit(
			'setConfirmLeaving',
			false,
		);

	},
	methods: {
		load() {
			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.getDamageAssessorCaseDetail(this.$route.params.id)
				.then(response => {
					this.damageAssessorCase = response.data;
				})
				.catch(error => {
					console.log(error);

					this.showError();
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		selectImage(base64Content) {
			if (!base64Content || !this.isEditable) {
				return;
			}
			this.imageToRotate = base64Content;
			this.rotation = 0;
		},
		rotateImage() {
			this.rotation = this.rotation === 270 ? 0 : this.rotation + 90;
		},
		setRotatedImage(image) {
			if (!image || !this.imageToRotate) {
				return;
			}
			this.imageToRotate = image;
			this.rotation = 0;
		},
		confirmImage() {
			if (!this.imageToRotate) {
				return;
			}
			this.addPendingImage(this.imageToRotate);
			this.resetRotation();
		},
		cancelRotation() {
			this.resetRotation();
		},
		resetRotation() {
			this.imageToRotate = null;
			this.rotation = 0;
		},
		addPendingImage(base64Content) {
			const mimeType = this.getMimeType(base64Content);
			let extension = 'jpg';
			if (mimeType === 'image/png') {
				extension = 'png';
			}
			this.pendingImages.push({
				localKey: `new-${this.nextLocalId++}`,
				type: 'image',
				extension,
				category: null,
				base64content: base64Content,
			});
		},
		removeImage(image) {
			if (!image?.canDelete) {
				return;
			}
			if (String(image.localKey).startsWith('new-')) {
				this.pendingImages = this.pendingImages.filter(item => item.localKey !== image.localKey);
				return;
			}
			if (image.id && !this.deletedMediaIds.includes(image.id)) {
				this.deletedMediaIds.push(image.id);
			}
		},
		openImage(image) {
			const index = this.visibleImages.findIndex(item => item.localKey === image.localKey);

			if (index !== -1) {
				this.lightboxIndex = index;
			}
		},
		buildDto() {
			const media = {};

			if (this.pendingImages.length) {
				media.add = this.pendingImages.map(image => ({
					type: image.type,
					extension: image.extension,
					category: null,
					base64content: image.base64content,
				}));
			}

			if (this.deletedMediaIds.length) {
				media.delete = [...this.deletedMediaIds];
			}

			return {
				media,
			};
		},
		save() {
			if (this.isSaving || !this.hasChanges) {
				return;
			}
			this.isSaving = true;

			new DamageAssessorCaseRepository()
				.updateDamageAssessorCase(this.damageAssessorCase.id, this.buildDto())
				.then(() => {
					this.$root.$emit('updateCase');
					this.$store.commit('setConfirmLeaving', false);
					this.$router.go(-1);
					return this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
				})
				.catch(error => {
					this.showError();
				})
				.finally(() => {
					this.isSaving = false;
				});
		},
		getMimeType(base64Content) {
			if (typeof base64Content !== 'string' || !base64Content.startsWith('data:')) {
				return '';
			}
			const endIndex = base64Content.indexOf(';');
			if (endIndex === -1) {
				return '';
			}
			return base64Content.substring(5, endIndex);
		},
		close() {
			if (this.imageToRotate) {
				this.cancelRotation();
				return;
			}
			this.$store.commit('setConfirmLeaving', false);
			this.$router.go(-1);
		},
		showError() {
			this.$swal(
				this.$t('general.error').toString(),
				this.$t('general.changes_not_saved').toString(),
				'error',
			);
		},
	},
};
</script>

<style scoped>
.photo-wrapper {
	min-height: 140px;
}

.photo-image {
	height: 200px;
	object-fit: cover;
}

.photo-delete-btn {
	position: absolute;
	top: 8px;
	right: 8px;

	width: 30px;
	height: 30px;

	display: flex;
	align-items: center;
	justify-content: center;

	background: rgba(255, 255, 255, 0.9);

	color: #4a5568;

	border-radius: 4px;
}

.rotation-workspace {
	width: 100%;
}

.rotation-preview {
	width: 100%;
	max-width: 700px;
	max-height: 60vh;

	margin-left: auto;
	margin-right: auto;

	overflow: auto;

	display: flex;
	align-items: flex-start;
	justify-content: center;
}
</style>
